import { Button } from '@progress/kendo-react-buttons'
import classNames from 'classnames'
import React from 'react'
import { Link } from 'react-router'
import { useToggle } from 'react-use'

import { Icon } from '~/components/elements/Icon'
import { VStack } from '~/components/elements/VStack'
import { type NavigationGroup } from '~/utils'

import styles from './AccordionLinkGroup.css'

export type AccordionLinkGroupProps<TGroupKey extends string> = {
  'data-testid'?: string
  group: NavigationGroup<TGroupKey>
}

export const AccordionLinkGroup = <TGroupKey extends string>({
  'data-testid': dataTestId = 'components-features-accordion-link-group',
  group,
}: AccordionLinkGroupProps<TGroupKey>) => {
  const { icon, isInitiallyExpanded, items, label } = group

  const [isExpanded, toggleIsExpanded] = useToggle(isInitiallyExpanded)

  if (items.length === 0) {
    return null
  }

  return (
    <VStack
      className={classNames(isExpanded && styles.expanded)}
      data-testid={dataTestId}
      spacing={0}
    >
      <Button
        className={classNames(
          styles.titleButton,
          !isExpanded &&
            items.some((item) => item.isActive) &&
            styles.titleButtonActive,
        )}
        fillMode="flat"
        onClick={toggleIsExpanded}
      >
        <Icon icon={icon} size="1rem" />

        <span className={styles.title}>{label}</span>

        <span className={styles.titleArrowIconContainer}>
          <Icon icon="arrow-chevron-right" size="1rem" />
        </span>
      </Button>

      <div
        className={styles.items}
        style={{ height: isExpanded ? items.length * 40 : 0 }}
      >
        {items.map((item) => (
          <Link
            key={item.to}
            className={classNames(item.isActive && styles.linkActive)}
            to={item.to}
          >
            <Button className={styles.linkButton} fillMode="flat">
              {item.label}
            </Button>
          </Link>
        ))}
      </div>
    </VStack>
  )
}
